<script setup lang="ts">
/**
 * 🅲🅾🅼🅿🅾🅽🅴🅽🆃 [HowYouRateDevice]
 * Модальное окно / экран "Как вы оцениваете ваше устройство"
 */

import { computed, nextTick, ref, useCssModule } from "vue";

import HandedDevice from "@/components/handedDevice/handedDevice.vue";
import Modal from "@/components/modal/modal.vue";
import { BugTrackerSection, useBugTracker } from "@/helpers/bugTracker";
import { deviceGradeNormalize } from "@/helpers/deviceGrade";
import { apiGetFirstQuestions } from "@/hooks/requests/apiGetQuestions/apiGetFirstQuestions";
import it4SendAnswerQuestion from "@/hooks/requests/it4SendAnswerQuestion/it4SendAnswerQuestion";
import {
    useAnalyticsEventImei,
    useAnalyticsEventPrePrice,
    useAnalyticsEventTiModel,
} from "@/hooks/useAnalytics/useAnalytics";
import useStoreResetter from "@/hooks/useStoreResetter/useStoreResetter";
import useTradeInCalculation from "@/hooks/useTradeInCalculation/useTradeInCalculation";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useChooseDeviceStore } from "@/stores/chooseDevice/chooseDevice";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
import { GRADES_DATA } from "@/stores/howYouRateDevice/howYouRateDevice.model";
import { useTIWStore } from "@/stores/tiw/tiw";
import Button from "@/ui/button/button.vue";
import RefreshIcon from "@/ui/icons/refresh.vue";
import Switch from "@/ui/switch/switch.vue";

const classes = useCssModule();

const TIWStore = useTIWStore();
const HowYouRateDeviceStore = useHowYouRateDeviceStore();
const ChooseDeviceStore = useChooseDeviceStore();

const pathToImages = import.meta.env.VITE_APP_URL;
const GET_DISCOUNT_RENDER_DATA = [
    {
        title: useTranslate("tiw__howYouRateDevice__howGetDiscountStep-1__title"),
        text: useTranslate("tiw__howYouRateDevice__howGetDiscountStep-1__text"),
        icon: `${pathToImages}/img/snap-pic.svg`,
    },
    {
        title: useTranslate("tiw__howYouRateDevice__howGetDiscountStep-2__title"),
        text: useTranslate("tiw__howYouRateDevice__howGetDiscountStep-2__text"),
        icon: `${pathToImages}/img/get-price.svg`,
    },
    {
        title: useTranslate("tiw__howYouRateDevice__howGetDiscountStep-3__title"),
        text: useTranslate("tiw__howYouRateDevice__howGetDiscountStep-3__text"),
        icon: `${pathToImages}/img/dont-miss.svg`,
    },
];

const loadingToNext = ref<boolean>(false);

function onEditHandler() {
    if (loadingToNext.value) return;

    HowYouRateDeviceStore.restoreToDefault();
    ChooseDeviceStore.restoreToDefault();
    TIWStore.switchStep("chooseDevice");
    useAnalyticsEventTiModel();
}
const handedDevicePrice = computed(() => {
    if (typeof HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale === "object") {
        return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale?.tradeInSale;
    }

    return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale;
});

async function selectRateHandler(_: MouseEvent, cid: string) {
    loadingToNext.value = true;

    useAnalyticsEventPrePrice();

    try {
        // Ответить на вопрос о статусах оценки
        const answerQuestionData = await it4SendAnswerQuestion({
            token: TIWStore.WIDGET_DATA.token, // "d7bd115c-e845-4381-8f11-8044cbc11dfe",
            deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId, // deviceID сдаваемого устройства
            lang: TIWStore.standardizedLocale,
            c: cid,
            ans: "",
            questionId: HowYouRateDeviceStore.questionData.questionId,
            from: "3",
        });

        // Устанавливаем значение грейда из ответа ответа на вопрос
        HowYouRateDeviceStore.grade = deviceGradeNormalize(answerQuestionData.device.grade);

        // Устанавливаем описание значения грейда "Как новый", "С дефектами"
        HowYouRateDeviceStore.rate = useTranslate(
            `tiw__grades__${GRADES_DATA[answerQuestionData.device.grade].gradeCode}_stateInfo`,
        );

        useTradeInCalculation({
            tradeInPrice: answerQuestionData.device.price,
        });
    } catch (e) {
        useBugTracker("HowYouRateDevice - it4SendAnswerQuestion", BugTrackerSection.ERROR, {
            exception: e.message,
            request: {
                token: TIWStore.WIDGET_DATA.token, // "d7bd115c-e845-4381-8f11-8044cbc11dfe",
                deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId, // deviceID сдаваемого устройства
                lang: TIWStore.standardizedLocale,
                c: cid,
                ans: "",
                questionId: HowYouRateDeviceStore.questionData.questionId,
                from: "3",
            },
        });
    }

    loadingToNext.value = false;
    if (document.startViewTransition) {
        document.startViewTransition(async () => {
            HowYouRateDeviceStore.isBack = false;
            await nextTick();
        });
    } else {
        HowYouRateDeviceStore.isBack = false;
    }
}

async function clickToNext() {
    if (TIWStore.IS_DISCOUNT_LATER) {
        HowYouRateDeviceStore.discountLaterFirstQuestion = await apiGetFirstQuestions({
            deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            lang: TIWStore.standardizedLocale,
            questionId: "",
            token: TIWStore.WIDGET_DATA.token,
        });
        TIWStore.switchStep("visualEvaluation");
    } else {
        TIWStore.switchStep("enterImei");
    }
    useAnalyticsEventImei();
}

async function clickRestartHandler() {
    useStoreResetter();
    TIWStore.INJECTED_TIW.current_api.onDrop();
    TIWStore.switchStep("chooseDevice");
    useAnalyticsEventTiModel();
}

async function clickToClose() {
    TIWStore.closeWidget();
    useStoreResetter();
    TIWStore.INJECTED_TIW.current_api.onDrop();
}
</script>

<template>
    <Modal class="tiw__step__howYouRateDevice">
        <template #header>
            <template v-if="HowYouRateDeviceStore.isBack">
                <p class="tiw__h2">
                    {{ HowYouRateDeviceStore.questionData.question }}
                </p>
                <p>{{ HowYouRateDeviceStore.questionData.description }}</p>
            </template>
            <template v-else>
                <p class="tiw__h2">
                    {{ HowYouRateDeviceStore.questionData.question }}
                </p>
            </template>
        </template>

        <div class="tiw__step__howYouRateDevice__body">
            <form name="howYouRateDeviceForm">
                <HandedDevice
                    :device-price="handedDevicePrice"
                    :device-chain="ChooseDeviceStore.deviceChain"
                    :device-state="!HowYouRateDeviceStore.isBack && HowYouRateDeviceStore.rate"
                    :device-preview="ChooseDeviceStore.deviceImagePreview"
                    :device-grade="null"
                    :device-no-grade="false"
                    :device-terminated="false"
                    :on-edit="onEditHandler"
                />
                <div
                    v-if="HowYouRateDeviceStore.isBack"
                    class="tiw__rate-chooser"
                    :class="{
                        'tiw__rate-chooser_loading': loadingToNext,
                    }"
                >
                    <div
                        v-for="chooserItem in HowYouRateDeviceStore.questionData.options"
                        :key="chooserItem.cid"
                        class="tiw__rate-chooser__element"
                    >
                        <input
                            :id="`howYouRateDeviceForm__rate_${chooserItem.cid}`"
                            v-model="HowYouRateDeviceStore.rate"
                            class="tiw_hidden"
                            type="radio"
                            name="rate"
                            :value="chooserItem.cname"
                        />
                        <label
                            :for="`howYouRateDeviceForm__rate_${chooserItem.cid}`"
                            class="tiw__rate-chooser__item"
                            @click="selectRateHandler($event, chooserItem.cid)"
                        >
                            <figure class="tiw__rate-chooser__figure">
                                <img :src="chooserItem.imageVariant" :alt="chooserItem.cname" />
                            </figure>
                            <div class="tiw__rate-chooser__body">
                                <p class="tiw__rate-chooser__title">
                                    {{ chooserItem.cname }}
                                </p>
                                <p class="tiw__rate-chooser__text">
                                    {{ chooserItem.description }}
                                </p>
                            </div>
                        </label>
                    </div>
                </div>
            </form>

            <!-- Инфо-блок с пояснением как получить скидку -->
            <template v-if="!HowYouRateDeviceStore.isBack && !HowYouRateDeviceStore.isNoPhotoSet">
                <div class="tiw__step__howYouRateDevice__info">
                    <p class="tiw__title">
                        {{ useTranslate("tiw__howYouRateDevice__howGetSale") }}
                    </p>
                    <p v-if="TIWStore.isPickupOnly" class="tiw__text">
                        {{ useTranslate("tiw__howYouRateDevice__pickup_only_info") }}
                    </p>

                    <div :class="classes.getDiscount">
                        <div :class="classes.itemsContainer">
                            <div
                                v-for="(item, i) in GET_DISCOUNT_RENDER_DATA"
                                :key="'how_you_rate_' + i"
                                :class="classes.item"
                            >
                                <div :class="classes.icon">
                                    <img
                                        width="30"
                                        height="30"
                                        :src="item.icon"
                                        alt="how get icons"
                                    />
                                    <div
                                        v-if="i + 1 !== GET_DISCOUNT_RENDER_DATA.length"
                                        :class="classes.iconsSlash"
                                    />
                                </div>
                                <div :class="classes.info">
                                    <p :class="classes.title">
                                        {{ item.title }}
                                    </p>
                                    <p :class="classes.text">
                                        {{ item.text }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="TIWStore.hasDiscountLater" :class="classes.discountSwitch">
                        <Switch v-model="TIWStore.IS_DISCOUNT_LATER" />
                        <div :class="classes.info">
                            <p :class="classes.title">
                                {{ useTranslate("tiw__howYouRateDevice__getDiscountLater__title") }}
                            </p>
                            <p :class="classes.text">
                                {{ useTranslate("tiw__howYouRateDevice__getDiscountLater__text") }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="tiw_group tiw_group-sb">
                    <Button
                        type="button"
                        class="tiw__step__howYouRateDevice__btn-next tiw__btn-next"
                        :class="classes.buttonNext"
                        @click="clickToNext"
                    >
                        <span>{{ useTranslate("tiw__continue") }}</span>
                    </Button>
                </div>
            </template>

            <!-- Инфо-блок с пояснением что надо идти в магазин -->
            <template v-if="!HowYouRateDeviceStore.isBack && HowYouRateDeviceStore.isNoPhotoSet">
                <div class="tiw__step__howYouRateDevice__info">
                    <p class="tiw__title">
                        {{ useTranslate("tiw__howYouRateDevice__howGetSale") }}
                    </p>
                    <p class="tiw__text">
                        {{ useTranslate("tiw__howYouRateDevice__howGetSale_info_goShop") }}
                    </p>
                </div>
                <div class="tiw_group tiw_group-sb">
                    <Button
                        type="button"
                        variant="skeleton"
                        class="tiw__step__howYouRateDevice__btn-restart"
                        @click="clickRestartHandler"
                    >
                        <RefreshIcon />
                        <span>{{ useTranslate("tiw__restart") }}</span>
                    </Button>
                    <Button
                        type="button"
                        class="tiw__step__howYouRateDevice__btn-next tiw__btn-close"
                        @click="clickToClose"
                    >
                        <span>{{ useTranslate("tiw__close") }}</span>
                    </Button>
                </div>
            </template>
        </div>
    </Modal>
</template>

<style lang="scss" src="./howYouRateDevice.scss"></style>
<style module>
.discountSwitch {
    display: flex;
    margin-bottom: 24px;
    font-size: calc(var(--custom-font-size) * 1);
    margin-top: 32px;

    p {
        margin: 0;
    }

    .info {
        margin-left: 16px;
    }
    .title {
        font-weight: 700;
    }
    .text {
        color: #808d9a;
        margin-top: 4px;
    }
}
.getDiscount {
    display: flex;
    margin-top: 16px;

    .itemsContainer {
        display: flex;
        flex-direction: column;
        font-size: calc(var(--custom-font-size) * 1);
        justify-content: start;

        p {
            margin: 0;
        }

        .item {
            display: flex;
            gap: 16px;

            .icon {
                display: flex;
                flex-direction: column;
                align-items: center;

                .iconsSlash {
                    width: 2px;
                    height: 100%;
                    min-height: 22px;
                    background: #d3dce9;
                    margin: 4px 0;
                }
            }

            .info {
                .title {
                    font-weight: 700;
                }
                .text {
                    color: #808d9a;
                    margin-top: 4px;
                }
            }
        }
    }
}
.buttonNext {
    width: 100%;
}
</style>
